import type { ISetArticleAvailabilityCmd } from "@/data/commands/operator/ISetArticleAvailabilityCmd";
import type { ISetArticleTempUnavailableCmd } from "@/data/commands/operator/ISetArticleTempUnavailableCmd";
import type { IUpdateAssignedTablesCmd } from "@/data/commands/operator/IUpdateAssignedTablesCmd";
import type { IUpdateBillManualEndUsersCountCmd } from "@/data/commands/operator/IUpdateBillManualEndUsersCountCmd";
import type { IUpdateBillNameCmd } from "@/data/commands/operator/IUpdateBillNameCmd";
import type { IRemoveGuestsFromTableCmd } from "@/data/commands/operator/RemoveGuestsFromTableCmd";
import type { ISetFulfillmentFulfilledCmd } from "@/data/commands/operator/SetFulfillmentFulfilledCmd";
import type { ISetFulfillmentReadyCmd } from "@/data/commands/operator/SetFulfillmentReadyCmd";
import type { IUpdateFulfillmentPositionCmd } from "@/data/commands/operator/UpdateFulfillmentPositionCmd";
import type { IUpdateFulfillmentPreparationTimeCmd } from "@/data/commands/operator/UpdateFulfillmentPreparationTimeCmd";
import type { IAcceptReservationCmd, IDeclineReservationCmd } from "@/data/commands/order/TransactionExportCmd";
import type { IOperatorSendOpenCashDrawerToPrinterCommand } from "@/data/commands/print/ISendOpenCashDrawerToPrinterCommand";
import { CatalogDao } from "@/data/dao/service/CatalogDao";
import type { IStockTableViewArticle } from "@/data/model/Article";
import type { IBillItemTimerDiscount } from "@/data/model/Bill";
import type { IDiscount } from "@/data/model/Discount";
import type { IBillTable, IOpenBill, IOperatorFulfillment, IPreorderShift, IRoomPlanRunningTimer, IRoomPlanTable, IRoomPlanTableTableReservation, ITableCovidUserCount } from "@/data/model/Operator";
import type { IPreorder, IReservationDetails, IReservationFutureStateActions, IReservationTable, IReservationTableAssignment, IReservationTableCombination, IReservationTablesToAssign } from "@/data/model/Order";
import type { IGetApplicableDiscountsQry } from "@/data/queries/operator/GetBillApplicableDiscountsQry";
import type { IGetOpenBillsForTableQuery } from "@/data/queries/operator/GetOpenBillsForTableQuery";
import type { IGetPreorderBusinessTodayQry, IGetPreorderShiftsQry } from "@/data/queries/operator/GetPreorderShiftsQry";
import type { IGetPreordersQry } from "@/data/queries/operator/GetPreordersQry";
import type { IGetTablesForReservationAssignmentQry } from "@/data/queries/operator/GetTablesForReservationAssignmentQry";
import type { IGetPreorderQry } from "@/data/queries/operator/IGetPreorderQry";
import type { IOpenBillsQuery } from "@/data/queries/operator/OpenBillsQuery";
import type { IOperatorFulfillmentsQry } from "@/data/queries/operator/OperatorFulfillmentsQry";
import type { IOperatorStockArticlesQry } from "@/data/queries/operator/OperatorStockArticlesQry";
import type { IGetTableReservationStateFilterOptionQuery, IRoomPlanQuery, ITableQuery as ITableDetailsQuery, ITableReservationStateFilter } from "@/data/queries/operator/TablesStateQuery";
import { DateTime } from "@/data/util/DateTime";



import { type IPaginatedResult } from "./BaseServiceDao";
import { BaseServiceDao, parseCollection, parseMultiLanguageText, parsePaginatedResult } from "./BaseServiceDao";
import { DiscountsDao } from "./DiscountsDao";
import { OrderDao } from "./OrderDao";
import { TableDao } from "./TableDao";
import { Operator } from "@/operator/context/Operator";


export class OperatorDao extends BaseServiceDao {
    public static className = "OperatorDao";

    constructor() {
        super("operator");
        this.isLocationDependent = true;
    }

    public async getTableReservationStateFilterOptions(
        qry: IGetTableReservationStateFilterOptionQuery
    ): Promise<ITableReservationStateFilter> {
        return this.get("table-reservation/state-filter-options", qry, (f) =>
            OperatorDao.parseTableReservationStateFilter(f)
        );
    }

    public async getRoomPlan(qry: IRoomPlanQuery): Promise<Array<IRoomPlanTable>> {
        return this.get("roomplan", qry, (f) => parseCollection(f, OperatorDao.parseRoomPlan));
    }

    public async getTableDetails(qry: ITableDetailsQuery): Promise<IRoomPlanTable> {
        return this.get("table-details", qry, OperatorDao.parseRoomPlan);
    }

    public async getOpenBills(qry: IOpenBillsQuery): Promise<Array<IOpenBill>> {
        return this.get("open-bills", qry, (f) => parseCollection(f, OperatorDao.parseOpenBill));
    }

    public async getOpenBillsForTable(tableId: string): Promise<Array<IOpenBill>> {
        const qry = {} as IGetOpenBillsForTableQuery;
        return this.get(`table/${tableId}/open-bills`, qry, (f) => parseCollection(f, OperatorDao.parseOpenBill));
    }

    public async getStockArticles(qry: IOperatorStockArticlesQry): Promise<IPaginatedResult<IStockTableViewArticle>> {
        return this.get("article/stock", qry, (f) => parsePaginatedResult(f, CatalogDao.parseStockTableViewArticle));
    }

    public async getFulfillments(qry: IOperatorFulfillmentsQry = {}): Promise<Array<IOperatorFulfillment>> {
        return this.get("fulfillment", qry, (f) => parseCollection(f, OperatorDao.parseOperatorFulfillment));
    }

    public async setFulfillmentReady(id: string, cmd: ISetFulfillmentReadyCmd): Promise<void> {
        return this.post(`fulfillment/${id}/ready`, cmd);
    }

    public async setFulfillmentFulfilled(id: string, cmd: ISetFulfillmentFulfilledCmd): Promise<void> {
        return this.post(`fulfillment/${id}/fulfilled`, cmd);
    }

    public async updateFulfillmentTime(id: string, cmd: IUpdateFulfillmentPreparationTimeCmd): Promise<void> {
        return this.put(`fulfillment/${id}/time`, cmd);
    }

    public async updateFulfillmentPosition(id: string, cmd: IUpdateFulfillmentPositionCmd): Promise<void> {
        return this.put(`fulfillment/${id}/position`, cmd);
    }

    public async removeGuests(cmd: IRemoveGuestsFromTableCmd): Promise<ITableCovidUserCount> {
        return this.post("removeguests", cmd, OperatorDao.parseTableCovidUserCount);
    }

    public async setArticleAvailability(id: string, cmd: ISetArticleAvailabilityCmd): Promise<IStockTableViewArticle> {
        return this.put(`article/${id}/availability`, cmd);
    }

    public async setArticleTempUnavailable(
        id: string,
        cmd: ISetArticleTempUnavailableCmd
    ): Promise<IStockTableViewArticle> {
        return this.put(`article/${id}/set-temporary-unavailable`, cmd);
    }

    public async updateBillName(id: string, cmd: IUpdateBillNameCmd) {
        return this.put(`bill/${id}/name`, cmd);
    }

    public async updateBillManualEndUsersCount(id: string, cmd: IUpdateBillManualEndUsersCountCmd) {
        return this.put(`bill/${id}/end-users-count`, cmd);
    }

    public async getPreorders(qry: IGetPreordersQry): Promise<Array<IPreorder>> {
        return this.post("preorder", qry, (f) => parseCollection(f, OperatorDao.parsePreorder));
    }

    public async getPreorder(id: string, qry: IGetPreorderQry): Promise<IPreorder> {
        return this.get(`preorder/${id}`, qry, OperatorDao.parsePreorder);
    }

    public async getPreorderShifts(qry: IGetPreorderShiftsQry): Promise<Array<IPreorderShift>> {
        return this.post("preorder-shift", qry, (f) => parseCollection(f, OperatorDao.parsePreorderShift));
    }

    public async getPreorderBusinessToday(qry: IGetPreorderBusinessTodayQry): Promise<Date> {
        return this.post("preorder-business-today", qry, (f) => DateTime.parseIsoDate(f));
    }

    public async getTablesForReservationAssignment(
        qry: IGetTablesForReservationAssignmentQry
    ): Promise<IReservationTablesToAssign> {
        return this.post("reservation-tables-to-assign", qry, OperatorDao.parseAvailableTablesOrCombinations);
    }

    public async updateAssignedTables(qry: IUpdateAssignedTablesCmd): Promise<IReservationDetails> {
        return this.post("assigned-tables", qry, OperatorDao.parseReservationDetails);
    }

    public async sendOpenCashDrawerToPrinterCommand(cmd: IOperatorSendOpenCashDrawerToPrinterCommand): Promise<void> {
        return this.post("send-open-cash-drawer-command", cmd);
    }

    public declineTableReservation(
        command: IDeclineReservationCmd,
        abortSignal?: AbortSignal
    ): Promise<IReservationDetails> {
        return this.put(`table-reservation/${command.tableReservationId}/decline`, command, (c) =>
            OperatorDao.parseReservationDetails(c)
        );
    }

    public acceptTableReservation(
        command: IAcceptReservationCmd,
        abortSignal?: AbortSignal
    ): Promise<IReservationDetails> {
        return this.put(`table-reservation/${command.tableReservationId}/accept`, command, (c) =>
            OperatorDao.parseReservationDetails(c)
        );
    }

    public static parseTableCovidUserCount(literal: any): ITableCovidUserCount {
        const base: ITableCovidUserCount = TableDao.parseTable(literal) as ITableCovidUserCount;
        base.allocations = parseInt(literal.allocations);
        return base;
    }

    public static parseRoomPlan(literal: any): IRoomPlanTable {
        const base: IRoomPlanTable = TableDao.parseTable(literal) as IRoomPlanTable;
        base.allocations = parseInt(literal.allocations);
        base.timestamp = literal.timestamp == null ? undefined : DateTime.parseIsoDate(literal.timestamp);
        base.status = parseInt(literal.status);
        base.openBillAmount = literal.openBillAmount == null ? undefined : parseFloat(literal.openBillAmount);
        base.manualEndUsersCount =
            literal.manualEndUsersCount == null ? undefined : parseInt(literal.manualEndUsersCount);
        base.openBillsCount = parseInt(literal.openBillsCount);
        // the server sends the elapsed time, so the device clock does not have to match the server's
        base.longestRunningTimerStartedDateTime =
            literal.longestRunningTimerElapsedSeconds == null
                ? undefined
                : new Date(Date.now() - literal.longestRunningTimerElapsedSeconds * 1000);
        base.openBillRunningTimers = parseCollection(literal.openBillRunningTimers, OperatorDao.parseRoomPlanRunningTimer);
        base.reservations = parseCollection(literal.reservations, OperatorDao.parseRoomPlanTableTableReservation);

        return base;
    }

    public static parseRoomPlanRunningTimer(literal: any): IRoomPlanRunningTimer {
        return {
            minuteRate: parseFloat(literal.minuteRate),
            elapsedSeconds: parseInt(literal.elapsedSeconds),
            // read now, like the elapsed time of a bill item: the device counts on from here with its own clock
            elapsedReadDateTime: new Date(),
            unitPrice: parseFloat(literal.unitPrice),
            unitPriceAfterDiscount: parseFloat(literal.unitPriceAfterDiscount),
            unpaidQuantity: parseInt(literal.unpaidQuantity),
            discounts: parseCollection(literal.discounts, OperatorDao.parseRoomPlanTimerDiscount),
        };
    }

    // the same as CustomerDao.parseBillItemTimerDiscount, kept here so this DAO does not import that one
    public static parseRoomPlanTimerDiscount(literal: any): IBillItemTimerDiscount {
        return {
            discountType: parseInt(literal.discountType),
            percentage: literal.percentage == null ? undefined : parseFloat(literal.percentage),
            absoluteAmount: literal.absoluteAmount == null ? undefined : parseFloat(literal.absoluteAmount),
        };
    }

    public static parseRoomPlanTableTableReservation(literal: any): IRoomPlanTableTableReservation {
        return {
            id: literal.id,
            orderId: literal.orderId,
            startDateTime: DateTime.parseIsoDate(literal.startDateTime),
            endDateTime: DateTime.parseIsoDate(literal.endDateTime),
            status: literal.status == null ? undefined : parseInt(literal.status),
        }
    }

    public static parseTableReservationStateFilter(literal: any): ITableReservationStateFilter {
        return {
            defaultSelections: parseCollection(literal.defaultSelections, (c) => c),
            filterOptions: parseCollection(literal.filterOptions, DiscountsDao.parseLinkableEntity),
        };
    }

    public static parseOperatorFulfillment(literal: any): IOperatorFulfillment {
        return {
            id: literal.id,
            locationId: literal.locationId,
            fulfillmentGroupId: literal.fulfillmentGroupId == null ? undefined : literal.fulfillmentGroupId,
            orderId: literal.orderId,
            orderNumber: literal.orderNumber,
            orderHasDeliveryAddress: literal.orderHasDeliveryAddress,
            dailyOrderIncrement:
                literal.dailyOrderIncrement == null ? undefined : parseInt(literal.dailyOrderIncrement),
            tableName: literal.tableName == null ? undefined : literal.tableName,
            useCaseName: literal.useCaseName == null ? undefined : parseMultiLanguageText(literal.useCaseName),
            roomId: literal.roomId,
            orderDate: DateTime.parseIsoDate(literal.orderDate),
            amendedReadyDateTime: DateTime.parseIsoDate(literal.amendedReadyDateTime),
            scheduledFulfillmentDateTime: DateTime.parseIsoDate(literal.scheduledFulfillmentDateTime),
            effectiveFulfillmentDateTime:
                literal.effectiveFulfillmentDateTime == null
                    ? undefined
                    : DateTime.parseIsoDate(literal.effectiveFulfillmentDateTime),
            effectiveReadyDateTime:
                literal.effectiveReadyDateTime == null
                    ? undefined
                    : DateTime.parseIsoDate(literal.effectiveReadyDateTime),
            isFulfilled: literal.isFulfilled,
            items: parseCollection(literal.items, OrderDao.parseOrderItem),
            isPaid: literal.isPaid,
            cancellationState: literal.cancellationState == null ? undefined : parseInt(literal.cancellationState),
            fulfillmentPositionDisplayMode: parseInt(literal.fulfillmentPositionDisplayMode),
            state: parseInt(literal.state),
            currentQueuePosition:
                literal.currentQueuePosition == null ? undefined : parseInt(literal.currentQueuePosition),
            orderedByWaiterName: literal.orderedByWaiterName != null ? literal.orderedByWaiterName : undefined,
            orderHasOtherFulfillments: literal.orderHasOtherFulfillments,
            isLinked: literal.isLinked,
            customTableName: literal.customTableName == null ? undefined : literal.customTableName,
        };
    }

    public static parseOpenBill(literal: any): IOpenBill {
        return {
            billId: literal.billId == null ? undefined : literal.billId,
            name: literal.name,
            billCustomName: literal.billCustomName == null ? undefined : literal.billCustomName,
            billType: parseInt(literal.billType),
            openSince: DateTime.parseIsoDate(literal.openSince),
            openBillAmount: parseFloat(literal.openBillAmount),
            openBillAmountAfterDiscounts: parseFloat(literal.openBillAmountAfterDiscounts),
            operatorName: literal.operatorName != null ? literal.operatorName : undefined,
            allocatedGuests: parseInt(literal.allocatedGuests),
            hasPreorder: literal.hasPreorder,
            latestPreorderDateTime:
                literal.latestPreorderDateTime == null
                    ? undefined
                    : DateTime.parseIsoDate(literal.latestPreorderDateTime),
            table: OperatorDao.parseBillTable(literal.table),
        };
    }

    public static parseBillTable(literal: any): IBillTable {
        return {
            locationId: literal.locationId,
            roomId: literal.roomId,
            roomName: literal.roomName,
            tableId: literal.tableId,
            tableName: literal.tableName,
            tableNumber: parseInt(literal.tableNumber),
            seats: parseInt(literal.seats),
        };
    }

    public static parsePreorder(literal: any): IPreorder {
        return {
            orderId: literal.orderId,
            locationId: literal.locationId,
            preorderDateTime: DateTime.parseIsoDate(literal.preorderDateTime),
            reservation:
                literal.reservation == null ? undefined : OperatorDao.parseReservationDetails(literal.reservation),
            hasTableReservation: literal.hasTableReservation,
            ordererName: literal.ordererName,
            hasNonReservationItem: literal.hasNonReservationItem,
            offerId: literal.offerId,
            roomId: literal.roomId,
            orderUseCaseId: literal.orderUseCaseId == null ? undefined : literal.orderUseCaseId,
        };
    }

    public static parseReservationDetails(literal: any): IReservationDetails {
        return {
            tableReservationId: literal.tableReservationId,
            orderItemId: literal.orderItemId,
            timeFrom: DateTime.parseIsoDate(literal.timeFrom),
            timeTo: DateTime.parseIsoDate(literal.timeTo),
            seats: parseInt(literal.seats),
            overridingArticleId: literal.overridingArticleId == null ? undefined : literal.overridingArticleId,
            tableAssignments: parseCollection(literal.tableAssignments, OperatorDao.parseReservationTableAssignment),
            acceptanceState: parseInt(literal.acceptanceState),
            attendanceState: literal.attendanceState == null ? undefined : parseInt(literal.attendanceState),
            assignmentState: literal.assignmentState == null ? undefined : parseInt(literal.assignmentState),
            futureStateActions: OperatorDao.parseReservationFutureStateActions(literal.futureStateActions),
            tableAssignmentAvailable: literal.tableAssignmentAvailable,
            canEditTableAssignments: literal.canEditTableAssignments,
        };
    }

    public static parseReservationFutureStateActions(literal: any): IReservationFutureStateActions {
        return {
            showSetArrivedAction: literal.showSetArrivedAction,
            showSetNoShowAction: literal.showSetNoShowAction,
            showSetPartiallyArrivedAction: literal.showSetPartiallyArrivedAction,
            showSetPartiallySeatedAction: literal.showSetPartiallySeatedAction,
            showSetSeatedAction: literal.showSetSeatedAction,
            showAcceptOrDeclineActions: literal.showAcceptOrDeclineActions,
            showSetAssignmentTableAsFixedAction: literal.showSetAssignmentTableAsFixedAction,
            showRemoveFixedAssignmentAction: literal.showRemoveFixedAssignmentAction,
            showSetNotArrivedAction: literal.showSetNotArrivedAction,
            showCompleteAttendanceAction: literal.showCompleteAttendanceAction,
            showConfirmAssignmentAction: literal.showConfirmAssignmentAction,
        };
    }

    public static parseReservationTableAssignment(literal: any): IReservationTableAssignment {
        return {
            isTableCombination: literal.isTableCombination,
            table: literal.table == null ? undefined : OperatorDao.parseReservationTable(literal.table),
            tableCombination:
                literal.tableCombination == null
                    ? undefined
                    : OperatorDao.parseReservationTableCombination(literal.tableCombination),
        };
    }

    public static parseReservationTable(literal: any): IReservationTable {
        return {
            tableId: literal.tableId,
            tableName: literal.tableName,
        };
    }

    public static parseReservationTableCombination(literal: any): IReservationTableCombination {
        return {
            combinationId: literal.combinationId,
            tableNames: literal.tableNames,
            tables: parseCollection(literal.tables, OperatorDao.parseReservationTable),
        };
    }

    public static parsePreorderShift(literal: any): IPreorderShift {
        return {
            id: literal.id,
            shiftName: literal.shiftName,
            isAllDay: literal.isAllDay,
            startUtc: DateTime.parseIsoDate(literal.startUtc),
            startString: literal.startString,
            endUtc: DateTime.parseIsoDate(literal.endUtc),
            endString: literal.endString,
            offerId: literal.offerId,
            roomIds: literal.roomIds ?? [],
            orderUseCaseIds: literal.orderUseCaseIds ?? [],
        };
    }

    public static parseAvailableTablesOrCombinations(literal: any): IReservationTablesToAssign {
        return {
            tables: parseCollection(literal.tables, OperatorDao.parseReservationTable),
            combinations: parseCollection(literal.combinations, OperatorDao.parseReservationTableCombination),
        };
    }
}
